// src/service/websocket/NiftyWebsocketApi.jsx

import { useEffect, useState } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { environment } from "environment/environment"; // absolute import

const DEFAULT_STOCK_API_WEBSOCKET_URL = environment.stockApiDayRangeDataWSUrl;
const ALL_STOCK_API_DATA_CHANNELS = [
    "options",
    "stockAnalysisData",
    "stockSectorAnalysisData",
    "sectorialIndicesData",
    "strategyIndicesData",
    "globalMarketData",
    "fiiDiiData",
    "foTopGainerLooserData",
    "blockDealsLargeStockData",
    "blockDealsTopStockData",
    "logicalIndicatorValueData",
    "buyerSellerStockData",
    "stockScannerData",
    "atlasScannerData",
    "stockOptionData",
    "stockPreOpenData",
    "sectorRsiBandScannerData",
];

const useStockApiWebsocket = (
    socketUrl = DEFAULT_STOCK_API_WEBSOCKET_URL,
    dataChannels = ALL_STOCK_API_DATA_CHANNELS
) => {
    const [options, setOptions] = useState([]);
    const [stockAnalysisData, setStockAnalysisData] = useState([]);
    const [stockSectorAnalysisData, setStockSectorAnalysisData] = useState([]);
    const [sectorialIndicesData, setSectorialIndicesData] = useState([]);
    const [strategyIndicesData, setStrategyIndicesData] = useState([]);
    const [globalMarketData, setGlobalMarketData] = useState([]);
    const [fiiDiiData, setFiiDiiData] = useState([]);
    const [foTopGainerLooserData, setFoTopGainerLooserData] = useState([]);
    const [blockDealsLargeStockData, setBlockDealsLargeStockData] = useState([]);
    const [blockDealsTopStockData, setBlockDealsTopStockData] = useState([]);
    const [logicalIndicatorValueData, setLogicalIndicatorValueData] = useState([]);
    const [buyerSellerStockData, setBuyerSellerStockData] = useState([]);
    const [stockScannerData, setStockScannerData] = useState([]);
    const [atlasScannerData, setAtlasScannerData] = useState([]);
    const [stockOptionData, setStockOptionData] = useState([]);
    const [stockPreOpenData, setStockPreOpenData] = useState([]);
    const [sectorRsiBandScannerData, setSectorRsiBandScannerData] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const dataChannelsKey = [...new Set(dataChannels)].sort().join("|");
    
    useEffect(() => {
        if (typeof window === "undefined") return; // SSR safety

        const stompClient = new Client({
            webSocketFactory: () => new SockJS(socketUrl),
            reconnectDelay: 5000,
            connectionTimeout: 10000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            discardWebsocketOnCommFailure: true,
            debug: () => { }, // disable debug logs
        });

        stompClient.onConnect = () => {
            setIsConnected(true);
            const requestedChannels = new Set(dataChannelsKey.split("|").filter(Boolean));
            const subscribeToJson = (channel, topicSuffix, updateData) => {
                if (!requestedChannels.has(channel)) return;

                const destination = environment.stoackApiTopic + topicSuffix;
                stompClient.subscribe(destination, (message) => {
                    try {
                        updateData(JSON.parse(message.body));
                    } catch (error) {
                        console.error(`Failed to parse ${destination}:`, error);
                    }
                });
            };

            subscribeToJson("options", environment.stockDayRangeDataTopic, setOptions);
            subscribeToJson("stockAnalysisData", environment.stockDataAnalysisTopic, setStockAnalysisData);
            subscribeToJson("stockSectorAnalysisData", environment.stockSectorTopic, setStockSectorAnalysisData);
            subscribeToJson("sectorialIndicesData", environment.sectorIndicestopic, setSectorialIndicesData);
            subscribeToJson("strategyIndicesData", environment.strategyIndiciesTopic, setStrategyIndicesData);
            subscribeToJson("globalMarketData", environment.globalMarketDataTopic, setGlobalMarketData);
            subscribeToJson("fiiDiiData", environment.fiiDiiDataTopic, setFiiDiiData);
            subscribeToJson("foTopGainerLooserData", environment.foTopGainerLooserTopic, setFoTopGainerLooserData);
            subscribeToJson("blockDealsLargeStockData", environment.blockDealsLargeStockTopic, setBlockDealsLargeStockData);
            subscribeToJson("blockDealsTopStockData", environment.blockDealsTopStockTopic, setBlockDealsTopStockData);
            subscribeToJson("logicalIndicatorValueData", environment.logicalIndicatorValueTopic, setLogicalIndicatorValueData);
            subscribeToJson("buyerSellerStockData", environment.buyerSellerStockTopic, setBuyerSellerStockData);
            subscribeToJson("stockScannerData", environment.stockScannerTopic, setStockScannerData);
            subscribeToJson("atlasScannerData", environment.atlasScannerTopic, setAtlasScannerData);
            subscribeToJson("stockOptionData", environment.stockOptionTopic, setStockOptionData);
            subscribeToJson("stockPreOpenData", environment.stockPreOpenDataTopic, setStockPreOpenData);
            subscribeToJson("sectorRsiBandScannerData", environment.sectorRsiBandScannerTopic, setSectorRsiBandScannerData);
        };

        stompClient.onStompError = (frame) => {
            console.error("Broker reported error:", frame.headers["message"]);
            console.error("Additional details:", frame.body);
        };

        stompClient.onWebSocketError = (error) => {
            setIsConnected(false);
            console.error("Stock websocket connection error:", error);
        };

        stompClient.onWebSocketClose = (event) => {
            setIsConnected(false);
            if (!event.wasClean) {
                console.warn(
                    `Stock websocket closed unexpectedly (${event.code}); reconnecting to ${socketUrl}`
                );
            }
        };

        stompClient.onDisconnect = () => {
            setIsConnected(false);
        };

        stompClient.activate();

        return () => {
            setIsConnected(false);
            if (stompClient.active) {
                void stompClient.deactivate().catch((error) => {
                    console.error("Failed to close stock websocket connection:", error);
                });
            }
        };
    }, [socketUrl, dataChannelsKey]);

    return { options, stockAnalysisData, stockSectorAnalysisData, sectorialIndicesData, strategyIndicesData, 
        isConnected, globalMarketData, fiiDiiData, foTopGainerLooserData, blockDealsLargeStockData, 
        blockDealsTopStockData, logicalIndicatorValueData, buyerSellerStockData, stockScannerData, atlasScannerData, 
        stockOptionData, stockPreOpenData, sectorRsiBandScannerData};
};

export default useStockApiWebsocket;
